import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { parseCurrencyNumber } from '../../../../Utils/formatCurrency';

import logoImg from '../../../../assets/Logo/main_logo.png';
import crownIconImg from '../../../../assets/Icon/project/crown_icon.png';
import kakaoIconImg from '../../../../assets/Icon/project/kakao_icon.png';
import banner1000ExampleImg from '../../../../assets/img/1000_example.png';
import formImg from '../../../../assets/img/form.png';
import heartImg from '../../../../assets/img/heart.png';
import emotionImg from '../../../../assets/img/emotion.png';
import primeBadgeImg from '../../../../assets/img/prime_badge.png';
import shareHandImg from '../../../../assets/img/share_hand.png';
import loadingLottie from '../../../../assets/Icon/lottie/Insider-loading.lottie';
import navbarExampleImg from '../../../../assets/Icon/project/navbar/navbar_example.png';
import profileDefaultImg from '../../../../assets/Icon/project/default-profile.png'; // Assuming a default profile image exists or I'll use a placeholder

interface PreviewPartnerProps {
  title: string;
  projectId: string;
  goalAmount: string;
  currency: 'KRW' | 'USD';
  mediaPreviewUrl: string;
  videoPreviewSrc?: string; // Add this
  storyHtml: string;
  userFullName: string;
  membership: string;
  onClose: () => void;
  onCreate: () => void;
  creating: boolean;
  creatingLabel: string;
}

const PreviewPartner: React.FC<PreviewPartnerProps> = ({
  title,
  projectId,
  goalAmount,
  currency,
  mediaPreviewUrl,
  storyHtml,
  userFullName,
  membership,
  onClose,
  onCreate,
  creating,
  creatingLabel,
  videoPreviewSrc,
}) => {
  const { t } = useTranslation('translation');
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const projectTitle = title || 'Project Title';

  const today = new Date();
  const dateString = `Created ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (KR)`;

  const DonateIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path d="M25.3327 18.6667C27.3193 16.72 29.3327 14.3867 29.3327 11.3333C29.3327 9.38841 28.5601 7.52315 27.1848 6.14788C25.8095 4.77262 23.9443 4 21.9993 4C19.6527 4 17.9993 4.66667 15.9993 6.66667C13.9993 4.66667 12.346 4 9.99935 4C8.05443 4 6.18917 4.77262 4.8139 6.14788C3.43863 7.52315 2.66602 9.38841 2.66602 11.3333C2.66602 14.4 4.66602 16.7333 6.66602 18.6667L15.9993 28L25.3327 18.6667Z" stroke="white" strokeWidth="2.66667" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  const EnvelopeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M11.666 2.33337H2.33268C1.68835 2.33337 1.16602 2.85571 1.16602 3.50004V10.5C1.16602 11.1444 1.68835 11.6667 2.33268 11.6667H11.666C12.3103 11.6667 12.8327 11.1444 12.8327 10.5V3.50004C12.8327 2.85571 12.3103 2.33337 11.666 2.33337Z" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12.8327 4.08337L7.60018 7.40837C7.42009 7.52121 7.21187 7.58105 6.99935 7.58105C6.78683 7.58105 6.57861 7.52121 6.39852 7.40837L1.16602 4.08337" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <PreviewDialog onPointerDown={(e) => e.stopPropagation()}>
      <PreviewHeader>
        <PreviewHeaderTitle>프로젝트 미리보기</PreviewHeaderTitle>
        <PreviewCloseButton type="button" onClick={onClose} aria-label="close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="#17171B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="#17171B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </PreviewCloseButton>
      </PreviewHeader>
      <PreviewNavbar src={navbarExampleImg} alt="Navbar" />
      <PreviewBody>
        <PreviewHeroWrapper>
          <PreviewHero $isVideo={!!videoPreviewSrc}>
            {mediaPreviewUrl ? (
              <>
                <PreviewHeroImg src={mediaPreviewUrl} alt="" />
                {membership !== 'general' && <PreviewPrimeBadge src={primeBadgeImg} alt="PRIME" />}
                <PreviewShareHand src={shareHandImg} alt="" />
              </>
            ) : videoPreviewSrc ? (
              <PreviewIframe
                src={videoPreviewSrc}
                title="video preview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <PreviewEmpty>{t('page.newProject.preview.emptyImage')}</PreviewEmpty>
            )}
          </PreviewHero>
        </PreviewHeroWrapper>

        <PreviewMainContent>
          <PreviewLeft>
            <PreviewStoryCard>
              <PreviewStoryTitle>STORY</PreviewStoryTitle>
              
              {/* Progress Bar for Story (Mock) */}

              <PreviewStoryBanner src={banner1000ExampleImg} alt={t('page.newProject.preview.bannerAlt')} />

              {storyHtml ? (
                <PreviewStoryHtml dangerouslySetInnerHTML={{ __html: storyHtml }} />
              ) : (
                <PreviewStoryEmpty>{t('page.newProject.preview.emptyStory')}</PreviewStoryEmpty>
              )}

              <PreviewStoryReactions src={emotionImg} alt={t('page.newProject.preview.reactionsAlt')} />
              <PreviewStoryComment src={formImg} alt={t('page.newProject.preview.commentAlt')} />
              <PreviewHeartIcon src={heartImg} alt={t('page.newProject.preview.likeAlt')} />
            </PreviewStoryCard>
          </PreviewLeft>

          <PreviewRight>
            {/* Project Info Card */}
            <RightCard>
              <ProjectTitle>{projectTitle}</ProjectTitle>
              <UserProfileRow>
                <UserProfileImg src={profileDefaultImg} onError={(e) => (e.currentTarget.style.backgroundColor = '#ccc')} />
                <UserInfoCol>
                    <UserName>{userFullName || 'User Name'}</UserName>
                    <UserDateRow>
                        <UserDate>{dateString}</UserDate>
                        <KeymanBadge>키맨을 찾고 있습니다</KeymanBadge>
                    </UserDateRow>
                </UserInfoCol>
              </UserProfileRow>
              <ShareButton type="button">
                <BtnIcon><img src={kakaoIconImg} alt="kakao" width="20" height="20" /></BtnIcon> 공유하기
              </ShareButton>
            </RightCard>

            {/* Sponsor Card */}
            <RightCard>
                <SponsorTitle>
                    <CrownIcon src={crownIconImg} alt="crown" />
                    첫번째 후원자가 되어 주세요!
                </SponsorTitle>
                <SponsorProgressWrap>
                    <SponsorBadge>0% 달성</SponsorBadge>
                    <SponsorProgressBar>
                        <SponsorProgressFill style={{ width: '0%' }} />
                    </SponsorProgressBar>
                </SponsorProgressWrap>
                <SponsorGoal>
                    목표 {currency === 'KRW' ? '￦' : '$'} {numericGoal.toLocaleString()} / mo.
                </SponsorGoal>
                <DonateButton type="button"><DonateIcon /> 후원하기</DonateButton>
            </RightCard>
          </PreviewRight>
        </PreviewMainContent>

        <PreviewFooterArea>
          <FooterLogo src={logoImg} alt="Mission Fund" />
          <FooterMission>
            <FooterMissionText>{t('page.newProject.preview.footerMissionKo')}</FooterMissionText>
            <FooterMissionText $blue>MissionFund helps the Body of Christ fulfill its mission.</FooterMissionText>
          </FooterMission>

          <FooterButtons>
            <FooterButton>{t('page.newProject.preview.footerButtons.receipt')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.donations')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.story')}</FooterButton>
            <FooterButton>FAQ</FooterButton>
          </FooterButtons>

          <FooterDesc>
            <p>{t('page.newProject.preview.footerInfo.line1')}</p>
            <p>{t('page.newProject.preview.footerInfo.line2')}</p>
            <p>{t('page.newProject.preview.footerInfo.line3')}</p>
          </FooterDesc>

          <FooterContact>
            {t('page.newProject.preview.footerContact.line1')}<br />
            {t('page.newProject.preview.footerContact.line2')}<br />
            {t('page.newProject.preview.footerContact.line3')}
          </FooterContact>
        </PreviewFooterArea>
      </PreviewBody>
      <PreviewFooter>
        <PreviewConfirm
          type="button"
          onClick={onCreate}
          disabled={creating}
          $loading={creating}
          aria-busy={creating}
          aria-label={creating ? creatingLabel : undefined}
        >
          {creating ? (
            <LoadingIcon
              src={loadingLottie}
              autoplay
              loop
              aria-hidden
              style={{ width: 12, height: 12 }}
            />
          ) : t('common.actions.confirm')}
        </PreviewConfirm>
      </PreviewFooter>
    </PreviewDialog>
  );
};

export default PreviewPartner;

// Styled Components (Copied and Adapted)
const PreviewDialog = styled.div`
  width: min(1200px, 95vw);
  height: 90vh;
  background: #f0f1f2;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const PreviewHeader = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
  flex-shrink: 0;
`;

const PreviewHeaderTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #17171b;
`;

const PreviewCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewNavbar = styled.img`
  width: 100%;
  height: auto;
  display: block;
  flex-shrink: 0;
`;

const PreviewBody = styled.div`
  flex: 1;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding-bottom: 0;
`;

const PreviewHeroWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16 / 9;
  height: auto;
  min-height: 380px;
  max-height: 520px;
  margin-top: 0;
  filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewHero = styled.div<{ $isVideo?: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  clip-path: ${({ $isVideo }) => ($isVideo ? 'none' : 'polygon(0 0, 100% 0, 100% 100%, 50% 93%, 0 100%)')};
`;

const PreviewPrimeBadge = styled.img`
  position: absolute;
  top: 0;
  left: 70px;
  width: 68px;
  height: auto;
  z-index: 1;
`;

const PreviewShareHand = styled.img`
  position: absolute;
  bottom: 0;
  right: 40px;
  width: 100px;
  height: auto;
  z-index: 1;
`;

const PreviewHeroImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

const PreviewEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 600;
`;

const PreviewMainContent = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  gap: 20px;
  margin-top: 24px;
`;

const PreviewLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const PreviewRight = styled.div`
  width: 240px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PreviewStoryCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 32px 24px;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.50);
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

const PreviewStoryTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 400;
  line-height: 150%;
  margin-bottom: 12px;
`;

const PreviewStoryBanner = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
  margin-bottom: 32px;
  cursor: pointer;
  display: block;
`;

const PreviewStoryHtml = styled.div`
  color: #171717;
  font-family: NanumGothic;
  font-size: 21px;
  font-style: normal;
  font-weight: 400;
  line-height: 37.8px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  margin-bottom: 60px;

  p,
  div,
  span,
  li,
  ul,
  ol {
    color: #171717;
    font-family: NanumGothic;
    font-size: 21px;
    font-style: normal;
    font-weight: 400;
    line-height: 37.8px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  figure[data-embed='story-image'] {
    margin: 16px 0;
    border-radius: 10px;
    overflow: hidden;
    background: #f0f1f2;
  }

  figure[data-embed='story-image'] > img[data-embed='image'] {
    max-width: 100%;
    height: auto;
    display: block;
  }
`;

const PreviewStoryReactions = styled.img`
  width: 100%;
  height: auto;
  margin-bottom: 24px;
  display: block;
`;

const PreviewStoryComment = styled.img`
  width: 100%;
  height: auto;
  display: block;
  cursor: pointer;
`;

const PreviewHeartIcon = styled.img`
  width: 44px;
  height: 44px;
  align-self: flex-end;
  margin-top: 20px;
  cursor: pointer;
`;

const PreviewStoryEmpty = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  padding: 40px 0;
`;

const PreviewFooterArea = styled.div`
  width: 100%;
  margin-top: 60px;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px 120px;
`;

const FooterLogo = styled.img`
  height: 54px;
  margin-bottom: 24px;
`;

const FooterMission = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const FooterMissionText = styled.div<{ $blue?: boolean }>`
  color: #27AAE2;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: 20px;
  font-style: normal;
  font-weight: 350;
  line-height: 30px;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  width: 100%;
  max-width: 700px;
`;

const FooterButton = styled.div`
  background: #888;
  color: #fff;
  padding: 12px 24px;
  border-radius: 6px;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: fit-content;
  white-space: nowrap;
`;

const FooterDesc = styled.div`
  text-align: left;
  color: #374151;
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  max-width: 700px;
  margin-top: 32px;
  
  p {
    margin-bottom: 16px;
    word-break: keep-all;
  }
`;

const FooterContact = styled.div`
  text-align: center;
  color: #6B7280;
  font-family: "Noto Sans KR";
  font-size: 12px;
  font-style: normal;
  font-weight: 350;
  line-height: 16px;
  max-width: 700px;
  width: 100%;
  border-top: 1px solid #aaa;
  padding-top: 24px;
  margin-top: 24px;
`;

const PreviewFooter = styled.div`
  height: 76px;
  padding: 0 24px;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  z-index: 10;
`;

const PreviewConfirm = styled.button<{ $loading?: boolean }>`
  width: 130px;
  height: 54px;
  padding: 10px;
  border-radius: 4px;
  border: none;
  background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#026478')};
  color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;

  &:hover {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#09525d')};
  }

  &:disabled {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#bcbcbc')};
    color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
    cursor: not-allowed;
  }
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

// New Components for Right Column
const RightCard = styled.div`
  background: #fff;
  border-radius: 24px;
  padding: 20px;
  box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.50);
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const ProjectTitle = styled.div`
  color: #171717;
  font-family: "Noto Sans KR";
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  margin-bottom: 12px;
  word-break: keep-all;
  word-wrap: break-word;
`;

const UserProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  min-width: 0;
`;

const UserProfileImg = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  background-color: #eee;
  flex-shrink: 0;
`;

const UserInfoCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  flex: 1;
`;

const UserName = styled.div`
  color: #323231;
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const UserDateRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    min-width: 0;
    max-width: 100%;
`;

const KeymanBadge = styled.div`
    border-radius: 9999px;
    background: #CCC;
    color: #F7F1F1;
    text-align: center;
    font-family: "Noto Sans KR";
    font-size: 12px;
    font-style: normal;
    font-weight: 350;
    line-height: 18px;
    padding: 2px 10px;
    margin-top: 4px;
`;

const UserDate = styled.div`
    color: #5B5B5A;
    font-family: "Noto Sans KR";
    font-size: 14px;
    font-style: normal;
    font-weight: 350;
    line-height: 21px;
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const ShareButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  background: #FFDD33; // Yellow
  border: none;
  font-size: 15px;
  font-weight: 600;
  color: #17171b;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
`;

const BtnIcon = styled.span`
    font-size: 20px;
`;

const SponsorTitle = styled.div`
  color: #171717;
  font-family: "Noto Sans KR";
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 27px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CrownIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const SponsorProgressWrap = styled.div`
  margin-bottom: 12px;
`;

const SponsorBadge = styled.div`
  color: #FFF;
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-style: normal;
  font-weight: 350;
  line-height: 20px;
  border-radius: 8px;
  background: #B3D334;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);
  padding: 4px 12px;
  display: inline-block;
  margin-bottom: 6px;
`;

const SponsorProgressBar = styled.div`
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
`;

const SponsorProgressFill = styled.div`
  height: 100%;
  background: #B3D334;
`;

const SponsorGoal = styled.div`
  color: #5B5B5A;
  font-family: "Noto Sans KR";
  font-size: 16px;
  font-style: normal;
  font-weight: 350;
  line-height: 24px;
  text-align: left;
  margin-bottom: 16px;
`;

const DonateButton = styled.button`
  width: 100%;
  height: 60px;
  border-radius: 8px;
  background: #2BB1E6; // Blue
  border: none;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: 24px;
  font-style: normal;
  font-weight: 350;
  line-height: 32px;
  color: #FFF;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
`;

// Story Progress (Left Column)
const StoryProgressWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const StoryProgressLabel = styled.div`
  font-size: 12px;
  color: #929292;
`;

const StoryProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
  max-width: 100px;
`;

const StoryProgressFill = styled.div`
  height: 100%;
  background: #2BC2E1;
`;

const StoryProgressValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #2BC2E1;
`;

const StoryProgressCount = styled.div`
  font-size: 12px;
  color: #929292;
`;
